import { AxiosResponse, isAxiosError } from 'axios'
import api from '../api/axios'
import { CantidadesNuevos, FiltrosBienes, RevisionPlanilla } from '../types/inventario'

/** El nombre de Content-Disposition: `filename*=utf-8''…` (con tildes) o `filename="…"`. */
function nombreDe(disposicion: string | undefined, porDefecto: string) {
  const codificado = /filename\*=utf-8''([^;]+)/i.exec(disposicion ?? '')?.[1]
  if (codificado) return decodeURIComponent(codificado)
  return /filename="([^"]+)"/.exec(disposicion ?? '')?.[1] ?? porDefecto
}

/**
 * Pide un archivo y lo guarda en el computador. Un error llega como blob: se lee como
 * JSON para que `mensajeDeError` muestre el `detail` del servidor.
 */
async function descargar(pedir: () => Promise<AxiosResponse<Blob>>, porDefecto: string) {
  let r: AxiosResponse<Blob>
  try {
    r = await pedir()
  } catch (error) {
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text())
      } catch {
        // No era JSON: queda el mensaje genérico.
      }
    }
    throw error
  }
  const url = URL.createObjectURL(r.data)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombreDe(r.headers['content-disposition'], porDefecto)
  enlace.click()
  URL.revokeObjectURL(url)
}

const conArchivo = (archivo: File) => {
  const datos = new FormData()
  datos.append('archivo', archivo)
  return datos
}

export const planillaService = {
  /** Los bienes de la lista (con sus filtros) como planilla para editar. */
  descargarBienes: (filtros: FiltrosBienes) =>
    descargar(() => api.get<Blob>('planillas/bienes/', { params: filtros, responseType: 'blob' }), 'bienes.xlsx'),
  /** La planilla para bienes nuevos, con justo esas filas y el Tipo puesto. */
  descargarPlantilla: (cantidades: CantidadesNuevos) =>
    descargar(() => api.get<Blob>('planillas/plantilla/', { params: cantidades, responseType: 'blob' }), 'bienes-nuevos.xlsx'),
  /** La misma planilla subida, con una columna «Error» para corregirla ahí. */
  descargarErrores: (archivo: File) =>
    descargar(() => api.post<Blob>('planillas/errores/', conArchivo(archivo), { responseType: 'blob' }), 'planilla-con-errores.xlsx'),
  revisar: async (archivo: File): Promise<RevisionPlanilla> =>
    (await api.post<RevisionPlanilla>('planillas/revisar/', conArchivo(archivo))).data,
  aplicar: async (archivo: File): Promise<RevisionPlanilla> =>
    (await api.post<RevisionPlanilla>('planillas/aplicar/', conArchivo(archivo))).data,
}
